import React, { useEffect, useMemo, useState } from "react";
import { Link as RouterLink } from "react-router-dom";
import {
  ArrowLeft, ArrowUp, ArrowDown, ChevronsUp, Loader2, Plus, Save, Trash2,
  ShoppingBag, ExternalLink, CheckCircle2, Eye, EyeOff, Folder,
} from "lucide-react";
import { Header } from "../components/Header";
import { api, formatApiErrorDetail, resolveAsset } from "../lib/api";

export default function AdminProducts() {
  const [items, setItems] = useState(null);
  const [categories, setCategories] = useState([]);
  const [error, setError] = useState("");
  const [creating, setCreating] = useState(false);
  const [filterCat, setFilterCat] = useState("__all__");

  const refresh = async () => {
    try {
      const [pRes, cRes] = await Promise.all([
        api.get("/admin/products"),
        api.get("/admin/product-categories"),
      ]);
      setItems(pRes.data);
      setCategories(cRes.data || []);
    } catch (e) {
      setError(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    }
  };

  useEffect(() => { refresh(); }, []);

  const remove = async (id) => {
    if (!window.confirm("Delete this product? This can't be undone.")) return;
    try {
      await api.delete(`/admin/products/${id}`);
      await refresh();
    } catch (e) {
      setError(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    }
  };

  const move = async (idx, delta) => {
    if (!items) return;
    const target = idx + delta;
    if (target < 0 || target >= items.length) return;
    const next = [...items];
    const [moved] = next.splice(idx, 1);
    next.splice(target, 0, moved);
    setItems(next);
    try {
      await api.post("/admin/products/reorder", { ids: next.map((p) => p.id) });
    } catch (e) {
      setError(formatApiErrorDetail(e.response?.data?.detail) || e.message);
      await refresh();
    }
  };

  // Jump a product straight to position #1. Same backend endpoint as
  // single-step moves — the index in the posted `ids` array becomes the
  // new sort_order, so splicing the row to index 0 is all we need.
  const moveToTop = async (idx) => {
    if (!items || idx <= 0 || idx >= items.length) return;
    const next = [...items];
    const [moved] = next.splice(idx, 1);
    next.unshift(moved);
    setItems(next);
    try {
      await api.post("/admin/products/reorder", { ids: next.map((p) => p.id) });
    } catch (e) {
      setError(formatApiErrorDetail(e.response?.data?.detail) || e.message);
      await refresh();
    }
  };

  const catNameById = useMemo(() => {
    const m = {};
    for (const c of categories) m[c.id] = c.name;
    return m;
  }, [categories]);

  const visibleItems = useMemo(() => {
    if (!items) return null;
    if (filterCat === "__all__") return items;
    if (filterCat === "__none__") {
      return items.filter((p) => {
        const ids = Array.isArray(p.category_ids) ? p.category_ids : [];
        const known = ids.filter((id) => catNameById[id]);
        return known.length === 0;
      });
    }
    return items.filter((p) => Array.isArray(p.category_ids) && p.category_ids.includes(filterCat));
  }, [items, filterCat, catNameById]);

  return (
    <div
      className="admin-chrome min-h-screen bg-white text-[var(--brand-ink)]"
      data-testid="page-admin-products"
    >
      <Header />
      <main className="mx-auto max-w-5xl px-5 py-10 sm:px-8 sm:py-14">
        <RouterLink
          to="/admin"
          className="inline-flex items-center gap-1.5 label-kicker text-[var(--brand-body)] hover:text-[var(--brand-ink)]"
        >
          <ArrowLeft size={12} /> Back to admin
        </RouterLink>

        <div className="mt-6 mb-8 flex flex-wrap items-end justify-between gap-4">
          <div>
            <div className="flex items-center gap-2 text-brand-accent">
              <ShoppingBag size={16} strokeWidth={2.5} />
              <span className="label-kicker">Products</span>
            </div>
            <h1 className="mt-2 font-display text-3xl font-black tracking-tight sm:text-4xl">
              Affiliate product cards.
            </h1>
            <p className="mt-2 max-w-2xl text-sm text-[var(--brand-body)]">
              Add Amazon (or any) products with your affiliate link. Paste{" "}
              <code className="font-mono">[[products]]</code> into your custom
              page body (e.g. <code className="font-mono">/page/amazon</code>)
              to render the grid below.
            </p>
          </div>
          <button
            type="button"
            onClick={() => setCreating(true)}
            data-testid="product-new"
            className="inline-flex items-center gap-2 rounded-md bg-[#0A0F17] px-4 py-2 font-display text-sm font-bold text-white"
          >
            <Plus size={14} /> New product
          </button>
        </div>

        <div className="mb-6 flex flex-wrap items-center gap-3 rounded-md border border-black/10 bg-[#F8FAFC] p-3">
          <label className="flex items-center gap-2">
            <Folder size={14} className="text-[var(--brand-body)]" />
            <span className="label-kicker text-[var(--brand-body)]">Filter</span>
            <select
              value={filterCat}
              onChange={(e) => setFilterCat(e.target.value)}
              data-testid="product-filter-category"
              className="rounded-md border border-black/15 bg-white px-2 py-1.5 text-sm outline-none focus:border-[#0A0F17]"
            >
              <option value="__all__">All ({items?.length || 0})</option>
              <option value="__none__">Uncategorised</option>
              {categories.map((c) => (
                <option key={c.id} value={c.id}>{c.name}</option>
              ))}
            </select>
          </label>
          <RouterLink
            to="/admin/product-categories"
            data-testid="product-manage-categories"
            className="ml-auto inline-flex items-center gap-1.5 rounded-md border border-black/10 bg-white px-3 py-1.5 font-display text-xs font-semibold hover:border-[#0A0F17]"
          >
            <Folder size={12} /> Manage categories →
          </RouterLink>
        </div>

        {error ? (
          <p
            className="mb-4 rounded-sm border-l-2 border-[#E63946] bg-[#E63946]/5 px-3 py-2 text-sm"
            data-testid="product-error"
          >
            {error}
          </p>
        ) : null}

        {creating ? (
          <ProductForm
            onCancel={() => setCreating(false)}
            onSaved={async () => { setCreating(false); await refresh(); }}
            setError={setError}
            categories={categories}
          />
        ) : null}

        {items === null ? (
          <p className="text-sm text-[var(--brand-body)]">Loading…</p>
        ) : items.length === 0 && !creating ? (
          <div
            className="rounded-md border border-dashed border-black/15 p-10 text-center"
            data-testid="product-empty"
          >
            <p className="font-display text-lg font-bold">No products yet.</p>
            <p className="mt-1 text-sm text-[var(--brand-body)]">
              Click "New product" above to add your first one.
            </p>
          </div>
        ) : visibleItems && visibleItems.length === 0 ? (
          <div
            className="rounded-md border border-dashed border-black/15 p-10 text-center"
            data-testid="product-filter-empty"
          >
            <p className="font-display text-lg font-bold">No products in this category.</p>
            <p className="mt-1 text-sm text-[var(--brand-body)]">
              Change the filter above, or assign products to this category.
            </p>
          </div>
        ) : (
          <div className="space-y-4" data-testid="product-list">
            {visibleItems.map((p, idx) => (
              <ProductRow
                key={p.id}
                product={p}
                onSaved={refresh}
                onDelete={() => remove(p.id)}
                onMoveUp={filterCat !== "__all__" || idx === 0 ? null : () => move(idx, -1)}
                onMoveDown={filterCat !== "__all__" || idx === items.length - 1 ? null : () => move(idx, 1)}
                onMoveToTop={filterCat !== "__all__" || idx === 0 ? null : () => moveToTop(idx)}
                setOuterError={setError}
                categories={categories}
                catNameById={catNameById}
              />
            ))}
          </div>
        )}
      </main>
    </div>
  );
}

function ProductForm({ onCancel, onSaved, setError, categories }) {
  const [form, setForm] = useState({
    title: "",
    description: "",
    price: "",
    image_url: "",
    affiliate_url: "",
    vendor: "amazon",
    cta_label: "",
    category_ids: [],
    active: true,
  });
  const [saving, setSaving] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.title.trim() || !form.affiliate_url.trim()) return;
    setSaving(true);
    setError("");
    try {
      await api.post("/admin/products", form);
      await onSaved();
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <form
      onSubmit={submit}
      className="mb-6 rounded-md border-2 border-[#0A0F17]/20 bg-[#F8FAFC] p-5 sm:p-6"
      data-testid="product-create-form"
    >
      <p className="label-kicker text-[var(--brand-body)]">New product</p>
      <ProductFields form={form} setForm={setForm} prefix="product-create" categories={categories} />
      <div className="mt-4 flex items-center justify-end gap-2">
        <button
          type="button"
          onClick={onCancel}
          className="label-kicker text-[var(--brand-body)] underline-offset-2 hover:underline"
        >
          Cancel
        </button>
        <button
          type="submit"
          disabled={!form.title.trim() || !form.affiliate_url.trim() || saving}
          data-testid="product-create-submit"
          className="inline-flex items-center gap-2 rounded-md bg-[#0A0F17] px-4 py-2 font-display text-sm font-bold text-white disabled:opacity-50"
        >
          {saving ? <Loader2 size={14} className="animate-spin" /> : <Plus size={14} />}
          {saving ? "Saving…" : "Add product"}
        </button>
      </div>
    </form>
  );
}

function ProductRow({ product, onSaved, onDelete, onMoveUp, onMoveDown, onMoveToTop, setOuterError, categories, catNameById }) {
  const [form, setForm] = useState({ ...product });
  const [saving, setSaving] = useState(false);
  const [savedFlash, setSavedFlash] = useState(false);

  useEffect(() => { setForm({ ...product }); }, [product]);

  const dirty = JSON.stringify(snap(form)) !== JSON.stringify(snap(product));

  const save = async (overrides) => {
    setSaving(true);
    setOuterError("");
    try {
      await api.put(`/admin/products/${product.id}`, { ...form, ...(overrides || {}) });
      setSavedFlash(true);
      setTimeout(() => setSavedFlash(false), 1500);
      await onSaved();
    } catch (err) {
      setOuterError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setSaving(false);
    }
  };

  const toggleActive = () => save({ active: !form.active });

  const img = form.image_url ? resolveAsset(form.image_url) : "";

  return (
    <div
      className={`rounded-md border p-4 ${form.active ? "border-black/10 bg-white" : "border-black/10 bg-black/5"}`}
      data-testid={`product-row-${product.id}`}
    >
      <div className="flex flex-wrap items-start gap-4">
        <div className="h-20 w-20 shrink-0 overflow-hidden rounded-md border border-black/10 bg-[#F8FAFC]">
          {img ? (
            <img src={img} alt="" className="h-full w-full object-contain p-1" />
          ) : (
            <div className="flex h-full w-full items-center justify-center">
              <ShoppingBag size={20} className="text-[var(--brand-body)]" />
            </div>
          )}
        </div>
        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-center gap-2">
            <p className="font-display text-base font-bold">{form.title || "(untitled)"}</p>
            {(() => {
              const ids = Array.isArray(product.category_ids) ? product.category_ids : [];
              const named = ids
                .map((id) => ({ id, name: (catNameById || {})[id] }))
                .filter((c) => c.name);
              if (named.length === 0) {
                return (
                  <span className="rounded-sm border border-black/15 px-1.5 py-0.5 label-kicker text-[var(--brand-body)]">
                    Uncategorised
                  </span>
                );
              }
              return named.map((c) => (
                <span
                  key={c.id}
                  className="rounded-sm bg-[#6CABDD] px-1.5 py-0.5 label-kicker text-white"
                  data-testid={`product-${product.id}-category-badge-${c.id}`}
                >
                  {c.name}
                </span>
              ));
            })()}
            {form.active ? null : (
              <span className="label-kicker rounded bg-black/10 px-1.5 py-0.5">Hidden</span>
            )}
            {form.price ? (
              <span className="font-display text-sm font-black text-[var(--brand-ink)]">
                · {form.price}
              </span>
            ) : null}
          </div>
          <a
            href={form.affiliate_url}
            target="_blank"
            rel="sponsored noopener noreferrer"
            className="mt-1 inline-flex items-center gap-1 label-kicker text-[var(--brand-body)] hover:text-[var(--brand-ink)]"
          >
            View link <ExternalLink size={11} />
          </a>
        </div>
        <div className="flex items-center gap-2 shrink-0">
          <button
            type="button"
            onClick={onMoveToTop}
            disabled={!onMoveToTop}
            aria-label="Move to top"
            title="Move to top"
            data-testid={`product-${product.id}-top`}
            className="rounded-md border border-black/10 p-1.5 hover:border-[#0A0F17] disabled:opacity-40"
          >
            <ChevronsUp size={12} />
          </button>
          <button
            type="button"
            onClick={onMoveUp}
            disabled={!onMoveUp}
            aria-label="Move up"
            data-testid={`product-${product.id}-up`}
            className="rounded-md border border-black/10 p-1.5 hover:border-[#0A0F17] disabled:opacity-40"
          >
            <ArrowUp size={12} />
          </button>
          <button
            type="button"
            onClick={onMoveDown}
            disabled={!onMoveDown}
            aria-label="Move down"
            data-testid={`product-${product.id}-down`}
            className="rounded-md border border-black/10 p-1.5 hover:border-[#0A0F17] disabled:opacity-40"
          >
            <ArrowDown size={12} />
          </button>
          <button
            type="button"
            onClick={toggleActive}
            disabled={saving}
            data-testid={`product-${product.id}-toggle`}
            title={form.active ? "Hide from public" : "Show to public"}
            className="rounded-md border border-black/10 p-1.5 hover:border-[#0A0F17] disabled:opacity-50"
          >
            {form.active ? <Eye size={12} /> : <EyeOff size={12} />}
          </button>
          <button
            type="button"
            onClick={onDelete}
            data-testid={`product-${product.id}-delete`}
            className="rounded-md border border-[#E63946]/30 p-1.5 text-[#E63946] hover:bg-[#E63946]/10"
          >
            <Trash2 size={12} />
          </button>
        </div>
      </div>

      <details className="mt-4 group">
        <summary className="cursor-pointer label-kicker text-[var(--brand-body)] hover:text-[var(--brand-ink)]">
          Edit fields ▾
        </summary>
        <div className="mt-4">
          <ProductFields form={form} setForm={setForm} prefix={`product-${product.id}`} categories={categories} />
          <div className="mt-3 flex items-center justify-end gap-2">
            {savedFlash ? (
              <span className="inline-flex items-center gap-1 label-kicker text-[#16A34A]">
                <CheckCircle2 size={12} /> Saved
              </span>
            ) : null}
            <button
              type="button"
              onClick={() => save()}
              disabled={!dirty || saving}
              data-testid={`product-${product.id}-save`}
              className="inline-flex items-center gap-2 rounded-md bg-[#0A0F17] px-3 py-1.5 font-display text-xs font-bold text-white disabled:opacity-50"
            >
              {saving ? <Loader2 size={12} className="animate-spin" /> : <Save size={12} />}
              {saving ? "Saving…" : "Save"}
            </button>
          </div>
        </div>
      </details>
    </div>
  );
}

function ProductFields({ form, setForm, prefix, categories }) {
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));
  return (
    <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
      <Field label="Title *" testid={`${prefix}-title`}>
        <input
          type="text"
          required
          value={form.title || ""}
          onChange={set("title")}
          maxLength={200}
          placeholder="Coventry City 2025/26 Home Shirt"
          className="mt-1 block w-full rounded-md border border-black/15 bg-white px-3 py-2 text-sm outline-none focus:border-[#0A0F17]"
          data-testid={`${prefix}-title`}
        />
      </Field>
      <Field label="Price" testid={`${prefix}-price`}>
        <input
          type="text"
          value={form.price || ""}
          onChange={set("price")}
          maxLength={40}
          placeholder="£59.99"
          className="mt-1 block w-full rounded-md border border-black/15 bg-white px-3 py-2 text-sm outline-none focus:border-[#0A0F17]"
          data-testid={`${prefix}-price`}
        />
      </Field>
      <Field
        label="Button label (optional)"
        testid={`${prefix}-cta-label`}
      >
        <input
          type="text"
          value={form.cta_label || ""}
          onChange={set("cta_label")}
          maxLength={40}
          placeholder={
            form.vendor === "ebay" ? "Buy on eBay" :
            form.vendor === "amazon" ? "Buy on Amazon" :
            "Buy now"
          }
          className="mt-1 block w-full rounded-md border border-black/15 bg-white px-3 py-2 text-sm outline-none focus:border-[#0A0F17]"
          data-testid={`${prefix}-cta-label`}
        />
        <p className="mt-1 text-xs text-[var(--brand-body)]">
          Leave blank to use the default based on the vendor above.
        </p>
      </Field>
      <Field label="Categories" testid={`${prefix}-categories`} className="sm:col-span-2">
        {(categories || []).length === 0 ? (
          <p className="mt-1 text-xs text-[var(--brand-body)]">
            No categories yet. Create some in Admin → Product categories first.
          </p>
        ) : (
          <div
            className="mt-1 flex flex-wrap gap-2 rounded-md border border-black/15 bg-white p-2"
            data-testid={`${prefix}-categories`}
          >
            {(categories || []).map((c) => {
              const checked = Array.isArray(form.category_ids) && form.category_ids.includes(c.id);
              return (
                <label
                  key={c.id}
                  className={`inline-flex cursor-pointer items-center gap-1.5 rounded-sm border px-2 py-1 text-xs ${
                    checked
                      ? "border-[#6CABDD] bg-[#6CABDD]/15 text-[var(--brand-ink)]"
                      : "border-black/15 bg-white text-[var(--brand-body)] hover:border-[#0A0F17]"
                  }`}
                >
                  <input
                    type="checkbox"
                    checked={checked}
                    onChange={(e) => {
                      setForm((f) => {
                        const cur = Array.isArray(f.category_ids) ? [...f.category_ids] : [];
                        if (e.target.checked) {
                          if (!cur.includes(c.id)) cur.push(c.id);
                        } else {
                          const i = cur.indexOf(c.id);
                          if (i >= 0) cur.splice(i, 1);
                        }
                        return { ...f, category_ids: cur };
                      });
                    }}
                    data-testid={`${prefix}-category-${c.slug}`}
                    className="h-3.5 w-3.5 cursor-pointer"
                  />
                  <span className="font-display font-semibold">{c.name}</span>
                </label>
              );
            })}
          </div>
        )}
      </Field>
      <Field label="Description" testid={`${prefix}-description`} className="sm:col-span-2">
        <textarea
          value={form.description || ""}
          onChange={set("description")}
          maxLength={600}
          rows={2}
          placeholder="Short pitch — what is it, why fans want it."
          className="mt-1 block w-full rounded-md border border-black/15 bg-white px-3 py-2 text-sm outline-none focus:border-[#0A0F17]"
          data-testid={`${prefix}-description`}
        />
      </Field>
      <Field label="Affiliate URL *" testid={`${prefix}-affiliate-url`} className="sm:col-span-2">
        <input
          type="text"
          required
          value={form.affiliate_url || ""}
          onChange={set("affiliate_url")}
          maxLength={600}
          placeholder="https://amzn.to/3xYz123"
          className="mt-1 block w-full rounded-md border border-black/15 bg-white px-3 py-2 font-mono text-xs outline-none focus:border-[#0A0F17]"
          data-testid={`${prefix}-affiliate-url`}
        />
      </Field>
      <Field label="Image URL" testid={`${prefix}-image-url`} className="sm:col-span-2">
        <input
          type="text"
          value={form.image_url || ""}
          onChange={set("image_url")}
          maxLength={600}
          placeholder="https://… (paste image link from Amazon)"
          className="mt-1 block w-full rounded-md border border-black/15 bg-white px-3 py-2 font-mono text-xs outline-none focus:border-[#0A0F17]"
          data-testid={`${prefix}-image-url`}
        />
        <p className="mt-1 text-xs text-[var(--brand-body)]">
          On Amazon, right-click the product image and choose "Copy image
          address". External hotlinks are fine for Associates.
        </p>
      </Field>
    </div>
  );
}

function Field({ label, children, className = "" }) {
  return (
    <label className={`block ${className}`}>
      <span className="label-kicker text-[var(--brand-body)]">{label}</span>
      {children}
    </label>
  );
}

function snap(p) {
  const ids = Array.isArray(p.category_ids) ? [...p.category_ids] : [];
  ids.sort();
  return {
    title: p.title || "",
    description: p.description || "",
    price: p.price || "",
    image_url: p.image_url || "",
    affiliate_url: p.affiliate_url || "",
    vendor: p.vendor || "amazon",
    cta_label: p.cta_label || "",
    category_ids: ids,
    active: !!p.active,
  };
}
